import { Component, OnInit, inject, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { DateTime } from 'luxon';
import { BankStore } from '../../../core/stores/base.store';
import { ContactStore } from '../../../core/stores/base.store';
import { Bank, Contact, Loan } from '../../../core/models';

@Component({
  selector: 'app-loan-documentation',
  templateUrl: './loan-documentation.component.html',
  styleUrls: ['./loan-documentation.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule
  ]
})
export class LoanDocumentationComponent implements OnInit {
  loading = false;
  error: string | null = null;
  showAddressForm = true;
  showDocument = false;
  addressForm: FormGroup;
  
  // US States for dropdown
  states = [
    'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN',
    'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ',
    'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT',
    'WA', 'WI', 'WV', 'WY'
  ];
  
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private bankStore = inject(BankStore);
  private contactStore = inject(ContactStore);
  private fb = inject(FormBuilder);
  
  loanId = computed(() => this.bankStore.selectedLoan().id);
  
  // Document variables
  day: string = '';
  month: string = '';
  year: string = '';
  lenderName: string = '';
  lenderStreet: string = '';
  lenderCity: string = '';
  lenderState: string = '';
  lenderZip: string = '';
  contactName: string = '';
  contactStreet: string = '';
  contactCity: string = '';
  contactState: string = '';
  contactZip: string = '';
  principalAmount: string = '';
  interestRate: string = '';
  interestStartDate: string = '';
  monthlyPayment: string = '';
  paymentStartDate: string = '';
  paymentEndDate: string = '';
  gracePeriod: string = '';
  lateFee: string = '';
  defaultGracePeriod: string = '';
  defaultLateFee: string = '';
  demandPeriod: string = '';
  governingState: string = '';
  signingDay: string = '';
  signingMonth: string = '';
  signingYear: string = '';

  constructor() {
    this.addressForm = this.fb.group({
      // Borrower address
      borrowerStreet: [''],
      borrowerCity: [''],
      borrowerState: [''],
      borrowerZip: [''],
      
      // Bank address
      bankStreet: [''],
      bankCity: [''],
      bankState: [''],
      bankZip: ['']
    });
  }

  ngOnInit(): void {
    // Initialize the form with any existing data
    this.initializeFormWithData();
  }

  private initializeFormWithData(): void {
    if (!this.loanId) {
      this.error = 'Loan ID is required';
      return;
    }

    const loan = this.bankStore.selectedLoan();
    if (!loan) {
      this.error = 'Loan not found';
      return;
    }

    try {
      // Get the bank and contact
      const bank = this.bankStore.selected() as Bank;
      const contact = this.contactStore.getEntity(loan.contact_id) as Contact;

      if (!bank) {
        this.error = 'Bank information not available';
        return;
      }

      if (!contact) {
        this.error = 'Contact information not available';
        return;
      }

      // Set up names for the document
      this.lenderName = bank.name || '';
      this.contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
      
      // Initialize form with empty values since address info is not in the model
      this.addressForm.patchValue({
        borrowerStreet: '',
        borrowerCity: '',
        borrowerState: '',
        borrowerZip: '',
        
        bankStreet: '',
        bankCity: '',
        bankState: '',
        bankZip: ''
      });
      
    } catch (err) {
      console.error('Error initializing form:', err);
      this.error = 'An error occurred while initializing the form';
    }
  }

  onSubmitAddressForm(): void {
    if (this.addressForm.valid) {
      this.loading = true;
      
      // Get form values
      const formValues = this.addressForm.value;
      
      // Set address values from the form
      this.lenderStreet = formValues.bankStreet;
      this.lenderCity = formValues.bankCity;
      this.lenderState = formValues.bankState;
      this.lenderZip = formValues.bankZip;
      
      this.contactStreet = formValues.borrowerStreet;
      this.contactCity = formValues.borrowerCity;
      this.contactState = formValues.borrowerState;
      this.contactZip = formValues.borrowerZip;
      
      // Generate the rest of the documentation
      this.generateDocumentation();
      
      // Hide form and show document
      this.showAddressForm = false;
      this.showDocument = true;
    }
  }

  private generateDocumentation(): void {
    if (!this.loanId) {
      this.error = 'Loan ID is required';
      this.loading = false;
      return;
    }

    const loan = this.bankStore.selectedLoan();
    if (!loan) {
      this.error = 'Loan not found';
      this.loading = false;
      return;
    }

    try {
      // Set up date formatting
      const now = DateTime.now();
      this.day = this.addOrdinalSuffix(now.day);
      this.month = now.toFormat('MMMM');
      this.year = now.toFormat('yyyy');
      
      // Use the same date for signing date
      this.signingDay = this.addOrdinalSuffix(now.day);
      this.signingMonth = this.month;
      this.signingYear = this.year;
      
      // Format currency values
      this.principalAmount = this.formatCurrency(loan.amount);
      this.interestRate = loan.interest_rate?.toString() || '';
      this.monthlyPayment = this.formatCurrency(loan.payment);
      
      // Format dates
      let startDate: DateTime;
      if (loan.close_date) {
        startDate = typeof loan.close_date === 'string' 
          ? DateTime.fromISO(loan.close_date)
          : DateTime.fromJSDate(loan.close_date as Date);
      } else {
        startDate = now;
      }
      
      const endDate = startDate.plus({ months: loan.term || 0 });
      
      this.interestStartDate = startDate.toFormat('MMMM d, yyyy');
      this.paymentStartDate = startDate.plus({ months: 1 }).toFormat('MMMM d, yyyy');
      this.paymentEndDate = endDate.toFormat('MMMM d, yyyy');
      
      // Use actual values where available, empty strings otherwise
      this.gracePeriod = loan.grace_period?.toString() || '';
      this.lateFee = this.formatCurrency(loan.late_fee);
      this.defaultGracePeriod = '';
      this.defaultLateFee = '';
      this.demandPeriod = '';
      this.governingState = this.lenderState || '';
      
      this.loading = false;
    } catch (err) {
      console.error('Error generating documentation:', err);
      this.error = 'An error occurred while generating the loan documentation';
      this.loading = false;
    }
  }

  printDocumentation(): void {
    window.print();
  }

  downloadDocumentation(): void {
    try {
      // Get the loan document content
      const documentContent = document.querySelector('.loan-document');
      if (!documentContent) {
        console.error('Document content not found');
        return;
      }

      // Create a complete HTML document with proper styling
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Loan Agreement - ${this.contactName}</title>
          <style>
            ${this.getDocumentStyles()}
          </style>
        </head>
        <body>
          ${documentContent.outerHTML}
        </body>
        </html>
      `;

      // Create a blob and download link
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Loan_Agreement_${this.contactName.replace(/\s+/g, '_')}_${this.year}${this.month.substring(0, 3)}${this.day.replace(/\D/g, '')}.html`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading documentation:', err);
    }
  }

  openInNewWindow(): void {
    try {
      // Get the loan document content
      const documentContent = document.querySelector('.loan-document');
      if (!documentContent) {
        console.error('Document content not found');
        return;
      }

      // Create a complete HTML document with proper styling
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Loan Agreement - ${this.contactName}</title>
          <style>
            ${this.getDocumentStyles()}
          </style>
        </head>
        <body>
          ${documentContent.outerHTML}
        </body>
        </html>
      `;

      // Open a new window and write the content
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.open();
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      } else {
        console.error('Could not open new window. Popup blocker might be enabled.');
      }
    } catch (err) {
      console.error('Error opening documentation in new window:', err);
    }
  }

  private getDocumentStyles(): string {
    return `
      body {
        font-family: Arial, sans-serif;
        line-height: 1.6;
        margin: 40px;
      }
      .header {
        text-align: center;
        margin-bottom: 30px;
      }
      h1 {
        text-align: center;
        font-size: 24px;
        margin-bottom: 20px;
      }
      .parties {
        margin-bottom: 20px;
      }
      .party {
        margin-bottom: 15px;
      }
      .section {
        margin-bottom: 15px;
      }
      .section-title {
        font-weight: bold;
      }
      .signature {
        margin-top: 30px;
        border-top: 1px solid #ccc;
        padding-top: 10px;
      }
      .docusign {
        color: #777;
        font-size: 12px;
      }
      .footer {
        margin-top: 20px;
        font-size: 12px;
        color: #777;
        text-align: center;
      }
    `;
  }

  goBack(): void {
    this.router.navigate(['/banks']);
  }

  private formatCurrency(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return '';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  /**
   * Adds ordinal suffix to a day number (1st, 2nd, 3rd, etc.)
   */
  private addOrdinalSuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return day + 'th';
    }

    switch (day % 10) {
      case 1: return day + 'st';
      case 2: return day + 'nd';
      case 3: return day + 'rd';
      default: return day + 'th';
    }
  }
}
